/**
 * organView.js — «ТОЛЬКО ОРГАН В 3D»: мост от маски сегментации к объёмному
 * виду. [11.1.24, по слову владельца «проектируй»]
 *
 * ЗАЧЕМ. Пресет — фильтр по яркости: «покажи всё 25…130 HU» — и на нативе
 * почка тонет в мышцах той же плотности (предел метода, названный в титлах
 * 11.1.23). Сегментация — трафарет ФОРМЫ: воксели, помеченные врачом кистью
 * или пороговой кистью, — и трафарету плотность безразлична. Этот модуль
 * даёт маске третье измерение: тело гаснет, орган остаётся объёмно, рядом —
 * его объём в миллилитрах и STL в миллиметрах.
 *
 * КАК. Объёмное окно штатно умеет менять том (setVolumesForViewports — тем
 * же путём ходит восстановление после сна, viewport.js:1394). Маска УЖЕ
 * лежит в кэше томов Cornerstone (её оттуда же читает экспорт NIfTI):
 * подменяем том окна на маску, ставим передаточные функции «ступенькой»
 * вокруг номера сегмента — виден ровно он. «Вернуть тело» подменяет том
 * обратно; последний VRT-пресет пере-применяет вызывающий (featureBindings),
 * у которого есть доступ к памяти sidebarBindings.
 *
 * СТРОЕНИЕ. Чистая математика (подсчёт, объём, бинаризация) отделена от
 * cornerstone-клея и проверяется verify функционально на игрушечном массиве.
 */

import { getActiveSegmentationId, getSegmentationData } from '../tools/toolManager.js';
import { getRenderingEngine, cornerstone as _cs } from './viewportState.js';
import { cornerstoneTools as _csTools } from './viewportState.js';   // [11.1.38] событие «маска изменилась»
import { log } from '../utils/logger.js';

// [11.1.24] Чистая математика вынесена в organMath.js (ноль импортов):
// этот файл тянет toolManager, которому нужен window, — verify в node его
// не откроет, а числа проверять обязан. Реэкспорт сохраняет старые адреса.
import { подсчётВокселей, объёмМл, бинаризоватьСегмент, цветВRgb } from './organMath.js';
import { вырезатьПоРамке } from './maskMorph.js';   // [11.1.41] STL по вырезке, а не по всему тому
export { подсчётВокселей, объёмМл, бинаризоватьСегмент, цветВRgb };

// ══════════════════════════════════════════════════════════════
//  CORNERSTONE-КЛЕЙ
// ══════════════════════════════════════════════════════════════

/** Том маски активной сегментации — тем же путём, что экспорт NIfTI. */
export function томМаски() {
  const segId = getActiveSegmentationId();
  if (!segId) return { ошибка: 'Сегментации ещё нет: возьмите кисть и проведите по снимку' };
  const данные = getSegmentationData(segId);
  const L = данные?.representationData?.Labelmap;
  // [11.1.42] ПОЧЕМУ «МАСКА НЕ НАЙДЕНА В ПАМЯТИ» (жалоба владельца: не
  // работали ни «Экспорт NIfTI», ни «STL органа», ни рамка роста области).
  // Мы читали ТОЛЬКО L.volumeId. В Cornerstone 5 у labelmap появился слой
  // «labelmaps»: том может лежать в L.labelmaps[primaryLabelmapId].volumeId,
  // а верхнее поле остаться пустым (сверено с types/LabelmapTypes.ts —
  // LabelmapSegmentationDataShared.labelmaps). Тогда всё, что ходит через
  // томМаски, честно говорило «не найдена». Теперь перебираем все известные
  // места, а напоследок ищем в кеше по имени сегментации.
  const кэш = () => window.cornerstone?.cache || _cs?.cache;
  const взять = (id) => { try { return id ? кэш()?.getVolume?.(id) : null; } catch (e) { return null; } };
  const кандидаты = [L?.volumeId];
  try {
    const слои = L?.labelmaps || {};
    const главный = L?.primaryLabelmapId;
    if (главный && слои[главный]?.volumeId) кандидаты.push(слои[главный].volumeId);
    for (const к of Object.keys(слои)) if (слои[к]?.volumeId) кандидаты.push(слои[к].volumeId);
  } catch (e) { /* структура иная — пойдём поиском по кешу */ }
  let volumeId = null; let том = null;
  for (const к of кандидаты) { const т = взять(к); if (т) { volumeId = к; том = т; break; } }
  if (!том) {
    // Последний рубеж: том маски заводится нами с именем «<segId>-labelmap».
    try {
      for (const v of (кэш()?.getVolumes?.() || [])) {
        const id = String(v?.volumeId || '');
        if (id.includes(segId) || id.includes('labelmap')) { volumeId = id; том = v; break; }
      }
    } catch (e) { /* кеш недоступен */ }
  }
  if (!том) {
    log.warn(`[Маска] том не найден. segId=${segId}, кандидаты=[${кандидаты.filter(Boolean).join(', ') || 'пусто'}]`);
    return { ошибка: 'Маска не найдена в памяти — сегментация ещё строится?' };
  }
  const пиксели = том.voxelManager?.getCompleteScalarDataArray?.() || том.getScalarData?.();
  if (!пиксели?.length) return { ошибка: 'Маска пуста: рисовать ещё нечего' };
  return { том, volumeId, пиксели, dims: том.dimensions, spacing: том.spacing || [1, 1, 1] };
}

/**
 * [11.1.38] ЗАПИСЬ В МАСКУ + ОБНОВЛЕНИЕ ПОКАЗА. Морфология (заливка контуров,
 * интерполяция между срезами) считает на КОПИИ массива — getCompleteScalarData
 * Array в Cornerstone 5 возвращает копию, и правка «на месте» без этой
 * функции ушла бы в никуда. Здесь копия кладётся обратно и объявляется
 * изменённой, иначе экран показывает старую маску.
 * @returns {{ок:boolean, причина?:string}}
 */
export function записатьМаску(пиксели) {
  try {
    const segId = getActiveSegmentationId();
    if (!segId) return { ок: false, причина: 'Сегментации нет' };
    // [11.1.42] Тот же поиск тома, что в томМаски — двух дорог к одному
    // понятию не заводим (доктрина «один хозяин»).
    const найдено = томМаски();
    if (найдено.ошибка) return { ок: false, причина: найдено.ошибка };
    const том = найдено.том;
    if (typeof том.voxelManager?.setCompleteScalarDataArray === 'function') {
      том.voxelManager.setCompleteScalarDataArray(пиксели);
    } else if (том.getScalarData) {
      const свой = том.getScalarData();
      if (свой && свой !== пиксели) свой.set(пиксели);
    } else {
      return { ок: false, причина: 'Эта сборка не даёт записать маску' };
    }
    try { том.imageData?.modified?.(); } catch (e) { /* показ обновится событием ниже */ }
    try {
      const сег = _csTools?.segmentation;
      (сег?.triggerSegmentationEvents?.triggerSegmentationDataModified
        || сег?.triggerSegmentationDataModified)?.(segId);
    } catch (e) { /* событие необязательно — картинку добьёт рендер */ }
    try {
      const re = getRenderingEngine();
      re?.renderViewports?.(re.getViewports().map((v) => v.id));
    } catch (e) { /* */ }
    return { ок: true };
  } catch (e) {
    return { ок: false, причина: 'Запись маски: ' + e.message };
  }
}

// ══════════════════════════════════════════════════════════════
//  [11.1.40] ОТМЕНА ПОСЛЕДНЕЙ ПРАВКИ МАСКИ
//
//  Повод — живой случай владельца: рост области с широким диапазоном
//  протёк по всему животу (5 млн вокселей, обрезано лимитом) и МОЛЧА
//  влился в сегмент, где лежала аккуратная ручная работа на 617 мл.
//  Отделить утечку от обводок было нечем — только «Очистить» всё.
//
//  Хранится ОДНА ступень и ДЕЛЬТОЙ, а не копией маски: том 512×512×818
//  весит 214 МБ, снимок такого размера на телефоне убил бы вкладку.
//  Список изменённых индексов плюс прежние значения — единицы мегабайт.
// ══════════════════════════════════════════════════════════════

let _правка = null;   // { индексы: Int32Array, прежние: Uint8Array, что: string }

/** Запомнить правку маски: индексы изменённых вокселей и их прежние значения. */
export function запомнитьПравкуМаски(индексы, пиксели, что = 'правка', прежниеГотовые = null) {
  try {
    if (!индексы?.length) { _правка = null; return false; }
    const и = Int32Array.from(индексы);
    const п = new Uint8Array(и.length);
    // [11.1.40] Прежние значения либо приходят готовыми (морфология собирает
    // их ВНУТРИ операции — после неё массив уже изменён, снять поздно), либо
    // снимаются здесь, если вызвано ДО изменения (так делает рост области).
    if (прежниеГотовые?.length === и.length) {
      for (let k = 0; k < и.length; k++) п[k] = прежниеГотовые[k];
    } else {
      if (!пиксели?.length) { _правка = null; return false; }
      for (let k = 0; k < и.length; k++) п[k] = пиксели[и[k]];
    }
    _правка = { индексы: и, прежние: п, что: String(что) };
    return true;
  } catch (e) { _правка = null; return false; }
}

/** Есть ли что отменять и что именно — для подписи кнопки. */
export function естьПравкаМаски() { return _правка ? { что: _правка.что, вокселей: _правка.индексы.length } : null; }

/**
 * Отменить последнюю правку маски. ВНИМАНИЕ: прежние значения записываются
 * ДО правки, поэтому отмена возвращает ровно то, что было, включая ручные
 * мазки — она не «стирает сегмент», а откатывает одну операцию.
 */
export function отменитьПравкуМаски() {
  if (!_правка) return { ок: false, причина: 'Отменять нечего: последняя операция с маской не запомнена' };
  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };
  if (м.пиксели.length <= _правка.индексы[_правка.индексы.length - 1]) {
    _правка = null;
    return { ок: false, причина: 'Маска сменилась — отменять уже нечего' };
  }
  for (let k = 0; k < _правка.индексы.length; k++) м.пиксели[_правка.индексы[k]] = _правка.прежние[k];
  const зап = записатьМаску(м.пиксели);
  if (!зап.ок) return зап;
  const что = _правка.что; const n = _правка.индексы.length;
  _правка = null;   // одна ступень: второй раз отменять нечего
  return { ок: true, что, вокселей: n };
}

function _окно3D() {
  const re = getRenderingEngine();
  if (!re) return null;
  const тип3д = _cs?.Enums?.ViewportType?.VOLUME_3D || 'volume3d';
  return re.getViewports().find((v) => v.type === тип3д || v.type === 'volume3d') || null;
}

let _прежнее = null;   // { vpId, volumeId } — что стояло в окне до изоляции

/**
 * Показать в 3D-окне ТОЛЬКО сегмент №индекс: том окна подменяется маской,
 * прозрачность — ступенькой вокруг номера, цвет — цветом строки сегмента.
 * @returns {Promise<{ок:boolean, причина?:string, мл?:number, вокселей?:number}>}
 */
export async function изолироватьОрган({ индекс = 1, цвет = '#ef4444' } = {}) {
  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };
  const вокселей = подсчётВокселей(м.пиксели, индекс);
  if (!вокселей) return { ок: false, причина: `Сегмент ${индекс} пуст — сначала закрасьте орган кистью этим сегментом` };
  const vp = _окно3D();
  if (!vp) return { ок: false, причина: 'Нет объёмного окна: выберите раскладку с 3D (например, MPR+3D)' };

  const re = getRenderingEngine();
  try {
    if (!_прежнее) {
      const был = vp.getActors?.()?.[0]?.uid || vp.getDefaultActor?.()?.uid || null;
      _прежнее = { vpId: vp.id, volumeId: был };
    }
    await _cs.setVolumesForViewports(re, [{ volumeId: м.volumeId }], [vp.id], true);
    const актёр = vp.getDefaultActor?.()?.actor;
    const свойство = актёр?.getProperty?.();
    if (свойство) {
      const [r, g, b] = цветВRgb(цвет);
      const ctf = свойство.getRGBTransferFunction?.(0);
      if (ctf?.removeAllPoints) {
        ctf.removeAllPoints();
        ctf.addRGBPoint(0, 0, 0, 0);
        ctf.addRGBPoint(индекс, r, g, b);
      }
      const ofun = свойство.getScalarOpacity?.(0);
      if (ofun?.removeAllPoints) {
        // Ступенька: виден ровно этот номер — соседние сегменты прозрачны.
        ofun.removeAllPoints();
        ofun.addPoint(0, 0);
        ofun.addPoint(индекс - 0.5, 0);
        ofun.addPoint(индекс - 0.45, 0.92);
        ofun.addPoint(индекс + 0.45, 0.92);
        ofun.addPoint(индекс + 0.5, 0);
      }
      свойство.setShade?.(true);
      свойство.setAmbient?.(0.3);
      свойство.setDiffuse?.(0.75);
      свойство.setSpecular?.(0.1);
      свойство.setScalarOpacityUnitDistance?.(0, 1.0);
      свойство.setInterpolationTypeToLinear?.();
    }
    vp.render?.();
    return { ок: true, мл: объёмМл(м.пиксели, индекс, м.spacing), вокселей };
  } catch (e) {
    log.warn('[Орган-3D] изоляция:', e.message);
    return { ок: false, причина: 'Не удалось подменить том окна: ' + e.message };
  }
}

/** Вернуть в окно прежний том. Пресет пере-применяет вызывающий. */
export async function вернутьТело() {
  if (!_прежнее?.volumeId) return { ок: false, причина: 'Возвращать нечего: тело и не пряталось' };
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(_прежнее.vpId);
    if (!vp) return { ок: false, причина: 'Окно, где прятали тело, уже закрыто' };
    await _cs.setVolumesForViewports(re, [{ volumeId: _прежнее.volumeId }], [vp.id], true);
    vp.render?.();
    _прежнее = null;
    return { ок: true };
  } catch (e) {
    return { ок: false, причина: e.message };
  }
}

export function телоСпрятано() { return !!_прежнее; }

/**
 * STL органа: бинаризованная маска → marching cubes по ISO 0.5 (процессорный
 * путь vtkModule — он принимает шаг вокселя и отдаёт МИЛЛИМЕТРЫ) → файл.
 */
export async function stlОргана({ индекс = 1 } = {}) {
  const м = томМаски();
  if (м.ошибка) return { ок: false, причина: м.ошибка };
  // [11.1.41] ПОЧЕМУ «ПОВЕРХНОСТЬ НЕ ПОСТРОИЛАСЬ» (жалоба владельца).
  // Сетка строилась по ВСЕМУ тому: 512×512×818 = 214 млн вокселей, а
  // marching cubes требует под них ещё Int16-копию на 428 МБ. Браузер падал
  // по памяти, VTK возвращал пустой объект, и мы говорили «не построилась»
  // вместо «не хватило памяти». Орган занимает малую часть тома — считаем
  // по вырезке вокруг него и смещаем сетку обратно в координаты пациента.
  const вырезка = вырезатьПоРамке(м.пиксели, м.dims, индекс, 2);
  if (!вырезка) return { ок: false, причина: `Сегмент ${индекс} пуст — STL строить не из чего` };
  try {
    const { runMarchingCubes, exportSTL } = await import('../compute/vtkModule.js');
    const sp = м.spacing || [1, 1, 1];
    const origin = [вырезка.смещение[0] * sp[0], вырезка.смещение[1] * sp[1], вырезка.смещение[2] * sp[2]];
    log.info(`[STL] вырезка ${вырезка.dims.join('×')} вокселей вместо ${м.dims.join('×')} — по всему тому не хватало памяти`);
    const mesh = await runMarchingCubes(вырезка.бин, вырезка.dims, sp, 0.5, origin);
    if (!mesh?.points?.length) {
      return { ок: false, причина: 'Поверхность не построилась даже по вырезке: сообщите об этом — нужен размер вырезки из журнала ([STL] вырезка …)' };
    }
    const имя = `organ_seg${индекс}_${Date.now()}.stl`;
    const r = await exportSTL(mesh, имя);
    return r?.success
      ? { ок: true, имя, треугольников: Math.round((mesh.polys?.length || 0) / 4), sizeKb: r.sizeKb }
      : { ок: false, причина: 'Сетка построилась, а файл не записался' };
  } catch (e) {
    return { ок: false, причина: e.message };
  }
}

export default { подсчётВокселей, объёмМл, бинаризоватьСегмент, цветВRgb, томМаски, изолироватьОрган, вернутьТело, телоСпрятано, stlОргана };
